/*
 *   File : date-range-picker.js
 *   Author : https://evoqins.com
 *   Description : Date range picker component
 *   Integrations : react-calendar
 *   Version : 1.0.0
 */

// custom packages
import React, { useEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  subDays,
  subMonths,
  // subQuarters,
  // subWeeks,
  subYears,
  startOfMonth,
  endOfMonth,
} from "date-fns";

// custom component
import Icon from "../icons";

// custom style
import styles from "../../Style/Components/calender.module.scss";
// import '../../Style/global.scss'
const DATE_RANGE = [
  {
    id: 1,
    label: "Today",
  },
  {
    id: 2,
    label: "Yesterday",
  },
  {
    id: 3,
    label: "Last week",
  },
  {
    id: 4,
    label: "Last month",
  },
  {
    id: 5,
    label: "Last quarter",
  },
];

const DateRangePicker = (props) => {
  const calendarRef = useRef(null);

  const [value, setValue] = useState(props.value);
  const [showCalender, setShowCalender] = useState(false);
  const [selectedRangeId, setSelectedRangeId] = useState(0);
  const [activeStartDate, setActiveStartDate] = useState(props.value[0]);
  const [rotate, setRotate] = useState(false);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !calendarRef.current ||
        event.target.closest(`#${calendarRef.current.id}`)
      ) {
        return;
      }
      setShowCalender(false);
    };

    window.addEventListener("click", handleClickOutside);

    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    if (showCalender === true) {
      const react_calendar = document.querySelector(
        "#calender-range-picker-wrapper"
      );
      react_calendar.scrollIntoView({ behavior: "smooth", block: "end" });
      // document.body.style.overflow = "hidden";
    } else {
      // document.body.style.overflow = 'unset';
    }
  }, [showCalender]);

  //handle onchange date
  const _handleOnChangeDate = (date) => {
    setValue(date);
    props.onChange(date);
    setActiveStartDate(date[0]);
    setSelectedRangeId(0);
    setShowCalender(false);
  };

  const _handleShowCalender = () => {
    setShowCalender((showCalender) => !showCalender);
  };

  const _handleSelectRange = (range) => {
    const today = new Date();
    let start_date, end_date;
    switch (range.id) {
      case 1: // Today
        start_date = end_date = today;
        break;
      case 2: // Yesterday
        start_date = end_date = subDays(today, 1);
        break;
      case 3: // Last 7 days
        start_date = subDays(today, 6); // include today
        end_date = today;
        break;
      case 4: // Last 30 days
        start_date = subDays(today, 29);
        end_date = today;
        break;
      case 5: // Last 90 days
        start_date = subDays(today, 89);
        end_date = today;
        break;
      case 6: // Last 365 days
        start_date = subDays(today, 364);
        end_date = today;
        break;
      case 7: // Last month (calendar month)
        const lastMonth = subMonths(today, 1);
        start_date = startOfMonth(lastMonth);
        end_date = endOfMonth(lastMonth);
        break;
      case 8: // Last 12 months (rolling)
        start_date = subMonths(today, 12);
        end_date = today;
        break;
      case 9: // Last year (calendar year)
        const lastYear = subYears(today, 1);
        start_date = new Date(lastYear.getFullYear(), 0, 1);
        end_date = new Date(lastYear.getFullYear(), 11, 31);
        break;
      default:
        start_date = end_date = today;
    }
    setSelectedRangeId(range.id);
    setValue([start_date, end_date]);
    setActiveStartDate(start_date);
    props.onChange([start_date, end_date]);
  };

  const _handleResetRange = () => {
    setRotate(true);
    const today = null;
    setSelectedRangeId(0);
    setValue([today, today]);
    setActiveStartDate(today);
    props.onChange([today, today]);
    setTimeout(() => setRotate(false), 500);
  };

  const formatDate = (date) => {
    if (date) {
      return date
        .toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        })
        .replace(",", "");
    } else {
      return "";
    }
  };

  // format day according to the design
  function formatWeekDay(locale, date) {
    const day_format = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    const day_index = date.getDay();
    const formatted_day = day_format[day_index];
    return formatted_day;
  }

  return (
    <div
      id="date-picker-wrapper"
      className="position-relative "
      ref={calendarRef}
    >
      <div
        id="calender"
        className={`${props.ClassName} ${
          props.type === 2 ? "" : "e-border-1px-silver e-drop-shadow-linechart "
        } rounded-pill e-font-14 d-flex justify-content-between align-items-center py-2 e-cursor-pointer ${
          styles.e_input_range_wrapper
        } ${showCalender === true ? "border-deep-azure" : "border-gainsboro"} ${
          props.background ? props.background : "e-bg-transparent"
        } ${props.error && "border-red"}  position-relative `}
        onClick={_handleShowCalender}
      >
        {/* Displayed value */}
        {props.lableName ? (
          <p className="e-font-14 e-primary-font-poppins e-text-black mb-0">
            {props.lableName}
          </p>
        ) : (
          <>
            <Icon icon="calender" color="e-text-black" size={22} />
            <p
              className={`${
                !formatDate(value[0])
                  ? "opacity-50 e-font-14 e-font-weight-400 "
                  : "e-font-14"
              } e-font-14 ps-3 e-primary-font-poppins line-height-22px e-text-black  mb-0`}
            >
              {formatDate(value[0]) || "Start date"} -{" "}
              {formatDate(value[1]) || "End Date"}
            </p>
          </>
        )}

        <Icon
          icon={showCalender ? "chevron_up" : "drop-arrow-down"}
          size="20px"
          className="ps-2 "
        />
      </div>
      {props.error && (
        <div className="position-absolute d-flex align-items-center start-0">
          <p
            className={`e-text-light-carmine-pink e-font-12  mb-0 e-primary-font-poppins`}
          >
            {props.error}
          </p>
        </div>
      )}

      {/* Background overlay */}
      {showCalender === true && (
        <div
          id="calender-range-picker-wrapper"
          className={`${styles.e_calender} ${props.calenderPosition}   d-flex justify-content-between e-drop-calender-shadow gap-4 bg-white  e-calender position-absolute pb-3 top-100 z-3`}
        >
          {/* Range Select */}
          <div id="calender-range-picker-range" className="pe-4 mt-2">
            <div className="d-flex flex-column h-100">
              {DATE_RANGE.map((range, i) => (
                <span
                  key={i}
                  className={`${
                    selectedRangeId === range.id ? "e-bg-water-color" : ""
                  } e-cursor-pointer border-radius-4 date-range text-nowrap e-primary-font-poppins py-2 px-3 e-text-space-cadet e-font-16 e-font-weight-400 line-height-20px`}
                  onClick={() => _handleSelectRange(range)}
                >
                  {range.label}
                </span>
              ))}

              <div
                className="mt-auto ps-3 reset-icon pb-2 e-text-ruby e-cursor-pointer e-primary-font-poppins d-flex align-items-center e-font-16 e-font-weight-400"
                onClick={_handleResetRange}
              >
                <span className={rotate ? "spin" : ""}>
                  <Icon icon="reset" size="20px" />
                </span>
                <p className="m-0 ps-2 e-font-14 ">Reset Filter</p>
              </div>
            </div>
          </div>
          <Calendar
            value={value}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={({ activeStartDate }) =>
              setActiveStartDate(activeStartDate)
            }
            minDate={props.minDate}
            maxDate={props.maxDate}
            nextLabel={<Icon icon={"chevron_right"} size={"12px"} />}
            next2Label={null}
            prevLabel={<Icon icon={"chevron_left"} size={"12px"} />}
            prev2Label={null}
            showNeighboringMonth={false}
            // navigationLabel={({ date, label, locale, view }) => { return label.split(' ')[0] }}
            locale="en-IN"
            selectRange
            formatShortWeekday={formatWeekDay}
            onChange={_handleOnChangeDate}
            className={`border-0 e-primary-font-poppins ${styles.e_calender}`}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
